import {Select} from '@wandb/weave/components/Form/Select';
import React, {useState} from 'react';

import {useGetTrialPlanName} from '../../../../pages/TrialEndPage/useGetIsAcademicUser';
import {useAccountPrivileges} from '../../../../util/accounts/pricing';
import {useAdminModeActive} from '../../../../util/admin';
import {checkIsServiceAccount} from '../../../../util/serviceAccounts';
import {Account, AccountPlanType} from '../../../Search/SearchNav/types';
import {globalRegistryType} from '../../common/utils';
import {useOnTeamRegistryRoleChange} from './hooks';
import {
  isUserRow,
  RegistryMember,
  RegistryRole,
  RegistryRoles,
  RegistryTeamMember,
} from './types';
import {UserRegistryRoleCell} from './UserRegistryRoleCell';
import {
  customSelectComponents,
  CustomSingleValue,
  getRegistryRoleOptionFromRole,
  RegistryRoleOption,
  RegistryRolePill,
} from './userRegistryRoleUtils';
import {getStringFromRole} from './utils';

type RegistryMemberRoleCellProps = {
  member: RegistryMember | RegistryTeamMember;
  registry: globalRegistryType;
  isViewerRegistryAdmin?: boolean;
  selectedAccount: Account | null;
  enableRestrictedViewerRole: boolean;
};

export const RegistryMemberRoleCell = ({
  member,
  registry,
  isViewerRegistryAdmin,
  selectedAccount,
  enableRestrictedViewerRole,
}: RegistryMemberRoleCellProps) => {
  const isSiteAdmin = useAdminModeActive();
  const [isOpen, setIsOpen] = useState(false);

  const trialPlanName = useGetTrialPlanName();
  const isProTrial =
    selectedAccount?.accountPlanType === AccountPlanType.ThirtyDayTrial &&
    trialPlanName === 'Pro';

  const {loading: privilegesLoading, privileges} =
    useAccountPrivileges(selectedAccount);

  // role management is not enabled for trial users, but we want them to be able to
  // change roles to fully test registry
  const roleManagementEnabled = privileges.roleManagementEnabled || isProTrial;

  const {onTeamRegistryRoleChange, isUpdating} = useOnTeamRegistryRoleChange(
    registry.entityName,
    registry.registryProjectName
  );

  // These are common for users and team members
  const registryRoleOptions: RegistryRoleOption[] = [
    getRegistryRoleOptionFromRole(RegistryRoles.Admin, roleManagementEnabled),
    getRegistryRoleOptionFromRole(RegistryRoles.Member, roleManagementEnabled),
    getRegistryRoleOptionFromRole(RegistryRoles.Viewer, roleManagementEnabled),
  ];

  if (enableRestrictedViewerRole) {
    registryRoleOptions.push(
      getRegistryRoleOptionFromRole(
        RegistryRoles.RestrictedViewer,
        roleManagementEnabled
      )
    );
  }

  if (isUserRow(member)) {
    let filteredRegistryRoleOptions = registryRoleOptions;
    if (checkIsServiceAccount(member.accountType)) {
      // Remove the viewer & restricted viewer role options for service accounts
      filteredRegistryRoleOptions = filteredRegistryRoleOptions.filter(
        role =>
          role.value !== RegistryRoles.Viewer &&
          role.value !== RegistryRoles.RestrictedViewer
      );
    }
    // Assigning a new list of role options here instead of filtering out Member/Admin to
    // make it easier to add custom-role capabilities in the future
    if (member.viewOnlyRegistryAccess) {
      filteredRegistryRoleOptions = [
        getRegistryRoleOptionFromRole(
          RegistryRoles.Viewer,
          roleManagementEnabled
        ),
      ];
      if (enableRestrictedViewerRole) {
        filteredRegistryRoleOptions.push(
          getRegistryRoleOptionFromRole(
            RegistryRoles.RestrictedViewer,
            roleManagementEnabled
          )
        );
      }
    }
    return (
      <UserRegistryRoleCell
        user={member}
        registry={registry}
        isViewerRegistryAdmin={isViewerRegistryAdmin}
        registryRoleOptions={filteredRegistryRoleOptions}
        roleManagementEnabled={roleManagementEnabled}
        enableRestrictedViewerRole={enableRestrictedViewerRole}
      />
    );
  }
  const isViewerAdmin = isSiteAdmin || isViewerRegistryAdmin;
  const isReadOnly = !isViewerAdmin;
  if (isReadOnly) {
    return <RegistryRolePill role={member.role} />;
  }
  if (privilegesLoading) {
    return null;
  }
  return (
    <Select<RegistryRoleOption, false>
      aria-label="update user role"
      menuIsOpen={isOpen}
      onMenuOpen={() => setIsOpen(true)}
      onMenuClose={() => setIsOpen(false)}
      options={registryRoleOptions}
      formatOptionLabel={(option: RegistryRoleOption | null) => option?.value}
      value={getRegistryRoleOptionFromRole(
        member.role as RegistryRole,
        roleManagementEnabled
      )}
      onChange={async (selected: RegistryRoleOption | null) => {
        if (selected?.value) {
          await onTeamRegistryRoleChange(
            member.id,
            registry.id,
            getStringFromRole(selected.value)
          );
        }
      }}
      components={{
        ...customSelectComponents,
        SingleValue: props => (
          <CustomSingleValue {...props} isRegistryOwner={false} />
        ),
      }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      isSearchable={false}
      isLoading={isUpdating}
      isDisabled={isUpdating}
    />
  );
};
